import { computed, reactive } from 'vue';
import { request } from './catalog';
import { profileLocal } from './profiles/local';

/**
 * The privacy policy and the terms of use (edited in the admin panel, read on the documents
 * address). The app asks to accept them on first start, and again when the admin publishes a
 * significant change (the document's `consent` number grows). What was accepted is remembered in
 * this browser only: the server is never told who accepted.
 */
export interface LegalDocInfo {
  kind: 'privacy' | 'terms';
  title: { ru: string; en: string };
  version: number;
  consent: number;
  publishedAt: string | null;
  url: string;
}

const KEY = 'vp-legal-accepted';

function readAccepted(): Record<string, number> {
  try {
    const v = JSON.parse(profileLocal.getItem(KEY) ?? '{}') as Record<string, unknown>;
    return Object.fromEntries(Object.entries(v).filter((e): e is [string, number] => typeof e[1] === 'number'));
  } catch {
    return {};
  }
}

export const legalState = reactive({ docs: [] as LegalDocInfo[], accepted: readAccepted() });

/** A profile was opened: its own acceptance counts (each profile accepts the documents itself). */
export function reloadLegalForProfile(): void {
  legalState.accepted = readAccepted();
}

/** Ask the server (and the spare ones); without a server there is nothing to show or accept. */
export async function loadLegal(): Promise<void> {
  try {
    const { res, base } = await request('/api/v1/legal');
    if (!res.ok) return;
    const docs = ((await res.json()) as { docs: LegalDocInfo[] }).docs;
    // a page on the server itself (development, a spare server): its address goes first
    legalState.docs = docs.map((d) => ({ ...d, url: /^https?:\/\//.test(d.url) ? d.url : base + d.url }));
  } catch {
    /* offline: asked again next time */
  }
}

/** Some document has a version this browser has not accepted yet. */
export const needsConsent = computed(() => legalState.docs.some((d) => (legalState.accepted[d.kind] ?? 0) < d.consent));
/** Accepted before, a significant change since: "the documents were updated". */
export const legalUpdated = computed(() => legalState.docs.some((d) => (legalState.accepted[d.kind] ?? 0) > 0));

export function acceptLegal(): void {
  legalState.accepted = { ...legalState.accepted, ...Object.fromEntries(legalState.docs.map((d) => [d.kind, d.consent])) };
  try {
    profileLocal.setItem(KEY, JSON.stringify(legalState.accepted));
  } catch {
    /* private mode: asked again in the next session */
  }
}
